import type { Attendee } from '@littlebird/sdk';

const normalizeName = (name: string | null | undefined) =>
  name?.trim().toLowerCase() ?? '';

/**
 * The user's own tile is marked `self` so the backend can name the other
 * person in a one-on-one. The meeting app's own marker wins; the signed-in
 * user's name is the fallback, and with neither nobody is marked.
 */
export function attendeesFromParticipants(
  participants: string[],
  selfParticipant: string | null | undefined,
  userName: string | null | undefined
): Attendee[] {
  const normalizedSelf =
    normalizeName(selfParticipant) || normalizeName(userName);
  return participants.map((name) => ({
    email: null,
    display_name: name,
    response_status: null,
    organizer: null,
    self: normalizedSelf ? normalizeName(name) === normalizedSelf : null,
    resource: null,
    optional: null,
  }));
}

/**
 * Everyone who was in the call at any point stays an attendee, so people who
 * leave early are still named in the notes. Returns null when the roster adds
 * nothing new, so no update is sent.
 */
export function mergeAttendees(
  current: Attendee[] | null | undefined,
  incoming: Attendee[]
): Attendee[] | null {
  const merged = [...(current ?? [])];
  let changed = false;
  for (const attendee of incoming) {
    const index = merged.findIndex(
      (existing) =>
        normalizeName(existing.display_name) ===
        normalizeName(attendee.display_name)
    );
    if (index === -1) {
      merged.push(attendee);
      changed = true;
    } else if (merged[index].self === null && attendee.self !== null) {
      merged[index] = { ...merged[index], self: attendee.self };
      changed = true;
    }
  }
  return changed ? merged : null;
}
